import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Users, Eye, MapPin, Briefcase, Clock, DollarSign,
  CheckCircle, XCircle, Star, ExternalLink, Mail, Download,
  Filter, Search, Loader, Calendar, Building2, Award, Code, Phone,
  FileText, User, GraduationCap, Github, Linkedin, Globe, Trophy,
  Target, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VerifiedBadge from '../components/VerifiedBadge';
import useVerifiedBadges from '../hooks/useVerifiedBadges';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: 'bg-gray-100 text-gray-600',
  reviewed: 'bg-blue-100 text-blue-700',
  shortlisted: 'bg-amber-100 text-amber-700',
  interview: 'bg-purple-100 text-purple-700',
  rejected: 'bg-red-100 text-red-700',
  hired: 'bg-green-100 text-green-700'
};

const statusLabels = {
  pending: 'Pending',
  reviewed: 'Reviewed',
  shortlisted: 'Shortlisted',
  interview: 'Interview',
  rejected: 'Rejected',
  hired: 'Hired'
};

const CompanyJobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);

  const token = localStorage.getItem('company_token');
  const applicantBadges = useVerifiedBadges(applications.map(a => a.student_id));

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchJobDetail();
  }, [jobId, token, navigate]);

  const fetchJobDetail = async () => {
    try {
      const [jobRes, appsRes] = await Promise.all([
        axios.get(`${API}/api/company/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/company/jobs/${jobId}/applications`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setJob(jobRes.data);
      setApplications(appsRes.data.applications || []);
    } catch (err) {
      toast.error('Failed to load job details');
      navigate('/company/jobs');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/api/company/jobs/${jobId}/applications/export-csv`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const rows = res.data.csv_data || [];
      if (rows.length === 0) { toast.info('No applications to export'); return; }
      const headers = Object.keys(rows[0]);
      const csvContent = [
        headers.join(','),
        ...rows.map(row => headers.map(h => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${res.data.job_title || 'applications'}_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} applications`);
    } catch { toast.error('Export failed'); }
  };

  const updateApplicationStatus = async (applicationId, status) => {
    try {
      await axios.put(
        `${API}/api/company/jobs/${jobId}/applications/${applicationId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Application ${status === 'hired' ? 'marked as hired' : status}`);
      
      // Update local state
      setApplications(prev => prev.map(app => 
        app.id === applicationId ? { ...app, status } : app
      ));
      if (selectedApp?.id === applicationId) {
        setSelectedApp(prev => ({ ...prev, status }));
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    const matchesSearch = !searchQuery || 
      app.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.student_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.student_college?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getStatusCounts = () => {
    const counts = { all: applications.length };
    applications.forEach(app => {
      counts[app.status] = (counts[app.status] || 0) + 1;
    });
    return counts;
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="animate-spin text-purple-600" size={32} />
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/company/jobs" className="text-gray-400 hover:text-gray-600">
                <ArrowLeft size={20} />
              </Link>
              <div>
                <h1 className="font-bold text-gray-900">{job.title}</h1>
                <p className="text-xs text-gray-500">{applications.length} applications</p>
              </div>
            </div>
            <button
              onClick={exportCSV}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition-colors"
              data-testid="export-csv-btn"
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Applications List */}
          <div className="lg:col-span-2 space-y-4">
            {/* Filters */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search candidates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {['all', 'pending', 'shortlisted', 'interview', 'hired', 'rejected'].map(status => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        statusFilter === status
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {status === 'all' ? 'All' : statusLabels[status]} ({statusCounts[status] || 0})
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Applications */}
            {filteredApplications.length > 0 ? (
              <div className="space-y-3">
                {filteredApplications.map(app => (
                  <div
                    key={app.id}
                    onClick={() => setSelectedApp(app)}
                    className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md ${
                      selectedApp?.id === app.id ? 'border-purple-400 ring-2 ring-purple-100' : 'border-gray-200'
                    }`}
                    data-testid={`application-${app.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {app.student_name?.charAt(0) || 'U'}
                        </div>
                        <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900">{app.student_name || 'Unknown'}</h3>
                          <VerifiedBadge
                            isVerified={!!applicantBadges[app.student_id]}
                            planCode={applicantBadges[app.student_id]?.plan_code}
                            size="sm"
                          />
                        </div>
                          <p className="text-sm text-gray-500">{app.student_email}</p>
                          <p className="text-sm text-gray-400">{app.student_college}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[app.status]}`}>
                          {statusLabels[app.status]}
                        </span>
                        <p className="text-xs text-gray-400 mt-2">
                          {(() => {
                            const d = app.applied_at || app.created_at;
                            if (!d) return 'Date N/A';
                            try { return new Date(d).toLocaleDateString(); } catch { return 'Date N/A'; }
                          })()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      {app.student_score > 0 && (
                        <span className="flex items-center gap-1 text-amber-600">
                          <Award size={14} />
                          Score: {app.student_score}
                        </span>
                      )}
                      {app.student_rank && (
                        <span className="flex items-center gap-1 text-purple-600">
                          <Star size={14} />
                          Rank #{app.student_rank}
                        </span>
                      )}
                      {(app.student_phone || app.student?.phone) && (
                        <a
                          href={`tel:${app.student_phone || app.student?.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 text-green-600 hover:underline"
                        >
                          <Phone size={14} />
                          {app.student_phone || app.student?.phone}
                        </a>
                      )}
                      {(app.student_username || app.student?.username) && (
                        <Link 
                          to={`/codearena/profile/${app.student_username || app.student?.username}`}
                          target="_blank"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 text-cyan-600 hover:underline"
                        >
                          <Code size={14} />
                          View Profile
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <Users className="mx-auto text-gray-300 mb-4" size={48} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Applications Found</h3>
                <p className="text-gray-500">
                  {searchQuery || statusFilter !== 'all' 
                    ? 'Try adjusting your filters'
                    : 'No one has applied to this job yet'}
                </p>
              </div>
            )}
          </div>

          {/* Sidebar - Application Detail */}
          <div className="space-y-4">
            {selectedApp ? (
              <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto">
                {/* Candidate Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                    {selectedApp.student_name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-bold text-gray-900">{selectedApp.student_name}</h3>
                      <VerifiedBadge
                        isVerified={!!applicantBadges[selectedApp.student_id]}
                        planCode={applicantBadges[selectedApp.student_id]?.plan_code}
                        size="md"
                      />
                    </div>
                    <p className="text-sm text-gray-500">{selectedApp.student_college || selectedApp.student?.profile?.college}</p>
                    {selectedApp.student?.profile?.headline && (
                      <p className="text-xs text-gray-400 mt-0.5">{selectedApp.student?.profile?.headline}</p>
                    )}
                  </div>
                </div>

                {/* Contact Information */}
                <div className="mb-6">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Contact Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="text-gray-400" size={16} />
                      <a href={`mailto:${selectedApp.student_email}`} className="text-purple-600 hover:underline">
                        {selectedApp.student_email}
                      </a>
                    </div>
                    {(selectedApp.student_phone || selectedApp.student?.phone) && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="text-green-500" size={16} />
                        <a href={`tel:${selectedApp.student_phone || selectedApp.student?.phone}`} className="text-green-600 hover:underline font-medium">
                          {selectedApp.student_phone || selectedApp.student?.phone}
                        </a>
                      </div>
                    )}
                    {selectedApp.student?.profile?.location && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="text-gray-400" size={16} />
                        {selectedApp.student?.profile?.location}
                      </div>
                    )}
                  </div>
                </div>

                {/* Education */}
                <div className="mb-6">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Education</h4>
                  <div className="flex items-start gap-2">
                    <GraduationCap className="text-indigo-500 mt-0.5" size={16} />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedApp.student_college || selectedApp.student?.profile?.college || 'College not specified'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {selectedApp.student?.profile?.department && `${selectedApp.student?.profile?.department} • `}
                        {selectedApp.student?.graduation_year && `Class of ${selectedApp.student?.graduation_year}`}
                      </p>
                    </div>
                  </div>
                </div>

                {/* EvalentX Arena Stats */}
                {(selectedApp.student_score > 0 || selectedApp.student_rank) && (
                  <div className="mb-6">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">CodeArena Performance</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedApp.student_score > 0 && (
                        <div className="bg-amber-50 rounded-lg p-3 text-center">
                          <Award className="text-amber-500 mx-auto mb-1" size={20} />
                          <p className="text-lg font-bold text-amber-700">{selectedApp.student_score}</p>
                          <p className="text-xs text-amber-600">EvalentX Arena Score</p>
                        </div>
                      )}
                      {selectedApp.student_rank && (
                        <div className="bg-purple-50 rounded-lg p-3 text-center">
                          <Trophy className="text-purple-500 mx-auto mb-1" size={20} />
                          <p className="text-lg font-bold text-purple-700">#{selectedApp.student_rank}</p>
                          <p className="text-xs text-purple-600">National Rank</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Skills */}
                {(selectedApp.student?.profile?.skills?.length > 0 || selectedApp.student?.resume?.skills?.length > 0) && (
                  <div className="mb-6">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Skills</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {(selectedApp.student?.profile?.skills || selectedApp.student?.resume?.skills || []).slice(0, 10).map((skill, idx) => (
                        <span 
                          key={idx}
                          className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md"
                        >
                          {typeof skill === 'string' ? skill : skill.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Social Links */}
                {(selectedApp.student?.profile?.github || selectedApp.student?.profile?.linkedin || selectedApp.student?.profile?.portfolio) && (
                  <div className="mb-6">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Social Profiles</h4>
                    <div className="flex gap-2">
                      {selectedApp.student?.profile?.github && (
                        <a
                          href={selectedApp.student.profile.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition-colors"
                          title="GitHub"
                        >
                          <Github size={18} />
                        </a>
                      )}
                      {selectedApp.student?.profile?.linkedin && (
                        <a
                          href={selectedApp.student.profile.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
                          title="LinkedIn"
                        >
                          <Linkedin size={18} />
                        </a>
                      )}
                      {selectedApp.student?.profile?.portfolio && (
                        <a
                          href={selectedApp.student.profile.portfolio}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition-colors"
                          title="Portfolio"
                        >
                          <Globe size={18} />
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Cover Letter */}
                {selectedApp.cover_letter && (
                  <div className="mb-6">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Cover Letter</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap max-h-32 overflow-y-auto">
                      {selectedApp.cover_letter}
                    </p>
                  </div>
                )}

                {/* Application Info */}
                <div className="mb-6">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Application Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="text-gray-400" size={16} />
                      <span>Applied: {(() => {
                        const d = selectedApp.applied_at || selectedApp.created_at;
                        if (!d) return 'N/A';
                        try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return 'N/A'; }
                      })()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap className="text-gray-400" size={16} />
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[selectedApp.status] || 'bg-gray-100 text-gray-600'}`}>
                        {statusLabels[selectedApp.status] || 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Profile & Resume Links */}
                <div className="space-y-2 mb-6">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">View Full Profile</h4>
                  {(selectedApp.resume_url || selectedApp.student?.resume_url) && (
                    <a
                      href={selectedApp.resume_url || selectedApp.student?.resume_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors w-full font-medium"
                      data-testid="download-resume-btn"
                    >
                      <Download size={18} />
                      Download Resume (PDF)
                    </a>
                  )}
                  
                  {/* Always show profile links if student username exists */}
                  {(selectedApp.student_username || selectedApp.student?.username) && (
                    <>
                      <a
                        href={`/codearena/profile/${selectedApp.student_username || selectedApp.student?.username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2.5 bg-cyan-100 text-cyan-700 rounded-lg hover:bg-cyan-200 transition-colors w-full font-medium"
                        data-testid="view-arena-profile-btn"
                      >
                        <Code size={18} />
                        View EvalentX Arena Profile
                      </a>
                      <a
                        href={`/resume/${selectedApp.student_username || selectedApp.student?.username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors w-full font-medium"
                        data-testid="view-resume-btn"
                      >
                        <FileText size={18} />
                        View Verified Resume
                      </a>
                    </>
                  )}
                  
                  {/* If no resume or profile links, show email as contact option */}
                  {!selectedApp.resume_url && !selectedApp.student?.resume_url && !selectedApp.student_username && !selectedApp.student?.username && (
                    <div className="text-sm text-gray-400 py-2">
                      No resume or profile linked to this application.
                    </div>
                  )}
                </div>

                {/* Status Actions */}
                <div className="border-t border-gray-100 pt-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Update Status</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedApp.status !== 'shortlisted' && (
                      <button
                        onClick={() => updateApplicationStatus(selectedApp.id, 'shortlisted')}
                        className="px-3 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 text-sm font-medium"
                      >
                        Shortlist
                      </button>
                    )}
                    {selectedApp.status !== 'interview' && (
                      <button
                        onClick={() => updateApplicationStatus(selectedApp.id, 'interview')}
                        className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm font-medium"
                      >
                        Interview
                      </button>
                    )}
                    {selectedApp.status !== 'hired' && (
                      <button
                        onClick={() => updateApplicationStatus(selectedApp.id, 'hired')}
                        className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm font-medium"
                      >
                        <CheckCircle size={14} className="inline mr-1" />
                        Hire
                      </button>
                    )}
                    {selectedApp.status !== 'rejected' && (
                      <button
                        onClick={() => updateApplicationStatus(selectedApp.id, 'rejected')}
                        className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-medium"
                      >
                        <XCircle size={14} className="inline mr-1" />
                        Reject
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="text-center text-gray-500">
                  <Users className="mx-auto mb-3 text-gray-300" size={40} />
                  <p>Select an application to view details</p>
                </div>
              </div>
            )}

            {/* Job Info Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Job Details</h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin size={16} className="text-gray-400" />
                  {job.location}
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Briefcase size={16} className="text-gray-400" />
                  {job.job_type?.replace('_', ' ')} • {job.work_mode}
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock size={16} className="text-gray-400" />
                  {job.experience_level?.replace(/_/g, ' ')}
                </div>
                {(job.salary_min || job.salary_max) && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <DollarSign size={16} className="text-gray-400" />
                    {job.salary_currency} {job.salary_min?.toLocaleString()} - {job.salary_max?.toLocaleString()}
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-600">
                  <Eye size={16} className="text-gray-400" />
                  {job.views_count || 0} views
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CompanyJobDetailPage;
